import { EmptyState, TableWithGroupingsLoadingSkeleton } from '@repo/ui';
import { StudentsWorkloadAchievementGroup } from 'domain/models';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Button, Col, Row } from 'reactstrap';

import { normalizeText, pluralize } from '../helpers';
import { WorkloadAchievementGroup } from '../workload-achievement-group';

const GROUP_DEFAULT_OPEN = false;
const INITIAL_VISIBLE_GROUPS = 10;
const SHOW_MORE_STEP = 10;

interface IProps {
  groups: StudentsWorkloadAchievementGroup[];
  loading: boolean;
  loadingTotal?: boolean;
}

export const WorkloadAchievementGroupedList = ({
  groups,
  loading,
  loadingTotal = false,
}: IProps) => {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_GROUPS);
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});

  const getGroupKey = (groupIndex: number) => `group-${groupIndex}`;

  const hasSearch = Boolean(normalizeText(search));

  const filteredGroups = useMemo(() => {
    const term = normalizeText(search);

    if (!term) {
      return groups;
    }

    return groups.reduce<StudentsWorkloadAchievementGroup[]>(
      (accumulator, group) => {
        const rows = group.rows || [];

        if (normalizeText(group.groupName).includes(term)) {
          accumulator.push(group);
          return accumulator;
        }

        const matchedRows = rows.filter((row) =>
          normalizeText(row.studentName).includes(term),
        );

        if (matchedRows.length > 0) {
          accumulator.push({ ...group, rows: matchedRows });
        }

        return accumulator;
      },
      [],
    );
  }, [groups, search]);

  const displayedGroups = hasSearch
    ? filteredGroups
    : filteredGroups.slice(0, visibleCount);

  const canShowMore = !hasSearch && visibleCount < filteredGroups.length;

  const isOpen = useCallback(
    (key: string) => openStates[key] ?? GROUP_DEFAULT_OPEN,
    [openStates],
  );

  const toggleKey = useCallback((key: string) => {
    setOpenStates((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? GROUP_DEFAULT_OPEN),
    }));
  }, []);

  const handleToggleAll = useCallback(
    (shouldOpen: boolean) => {
      const nextOpenStates: Record<string, boolean> = {};

      displayedGroups.forEach((_, groupIndex) => {
        nextOpenStates[getGroupKey(groupIndex)] = shouldOpen;
      });

      setOpenStates(nextOpenStates);
    },
    [displayedGroups],
  );

  const isEverythingOpen = useMemo(
    () =>
      displayedGroups.length > 0 &&
      displayedGroups.every(
        (_, groupIndex) =>
          openStates[getGroupKey(groupIndex)] ?? GROUP_DEFAULT_OPEN,
      ),
    [displayedGroups, openStates],
  );

  const hasData = displayedGroups.length > 0;

  useEffect(() => {
    setOpenStates({});
    setVisibleCount(INITIAL_VISIBLE_GROUPS);
  }, [search]);

  const wasLoadingRef = useRef(loading);

  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      setOpenStates({});
      setVisibleCount(INITIAL_VISIBLE_GROUPS);
    }

    wasLoadingRef.current = loading;
  }, [loading]);

  return (
    <>
      <Row className="g-3 align-items-center mb-3">
        <Col xl={4} lg={5} md={6} sm={8} xs={12}>
          <div className="search-box">
            <input
              type="text"
              id="search"
              value={search}
              className="form-control search"
              placeholder="Pesquisar grupo ou estudante..."
              onChange={(event) => setSearch(event.target.value)}
            />
            <i className="ri-search-line search-icon"></i>
          </div>
        </Col>

        <Col className="d-flex align-items-center justify-content-end gap-3">
          {loading || loadingTotal ? (
            <Skeleton
              width={80}
              height={16}
              baseColor="#e8e8e8c5"
              highlightColor="#bababa96"
            />
          ) : (
            hasData && (
              <span className="text-muted fs-13">
                {pluralize(filteredGroups.length, 'grupo', 'grupos')}
              </span>
            )
          )}

          {!loading && hasData && (
            <Button
              size="sm"
              color="light"
              onClick={() => handleToggleAll(!isEverythingOpen)}
            >
              {isEverythingOpen ? (
                <>
                  <i className="ri-contract-up-down-line me-1"></i>
                  Recolher todos
                </>
              ) : (
                <>
                  <i className="ri-expand-up-down-line me-1"></i>
                  Expandir todos
                </>
              )}
            </Button>
          )}
        </Col>
      </Row>

      {loading && (
        <TableWithGroupingsLoadingSkeleton
          numberOfGroups={3}
          rowsPerGroup={3}
        />
      )}

      {!loading && !hasData && (
        <EmptyState
          variant="filter"
          title="Nenhum registro encontrado"
          description="Não há carga horária alcançada para os filtros aplicados."
          size={100}
          paddingY={3}
        />
      )}

      {!loading &&
        hasData &&
        displayedGroups.map((group, groupIndex) => (
          <WorkloadAchievementGroup
            group={group}
            key={`${groupIndex}-${group.groupHashId || ''}`}
            isOpen={isOpen(getGroupKey(groupIndex))}
            onToggle={() => toggleKey(getGroupKey(groupIndex))}
          />
        ))}

      {!loading && canShowMore && (
        <div className="d-flex justify-content-center mt-2">
          <Button
            size="sm"
            color="light"
            onClick={() => setVisibleCount((prev) => prev + SHOW_MORE_STEP)}
          >
            Mostrar mais
          </Button>
        </div>
      )}
    </>
  );
};
